import type { ComponentType } from 'react'
import { useTranslation } from 'react-i18next'
import { H3, Text, Button } from '@/components/atoms'
import {
  CoverageDiagnosticsIcon,
  CoverageProceduresIcon,
  CoverageInjuriesIcon,
  CoverageSkinConditionsIcon,
  CoverageCancerTreatmentIcon,
  CoverageXRaysIcon,
  CoverageBloodDisordersIcon,
  CoverageEndOfLifeCareIcon,
  CoverageVaccinesIcon,
  CoverageDeWormingIcon,
  CoverageFecalTestIcon,
  CoverageWellnessExamIcon,
  CoverageDentalCleaningIcon,
  CoverageHeartwormTestIcon,
  CoverageSpayNeuterIcon,
  CoverageMicrochippingIcon,
} from '@/components/atoms/icons/coverage-feature'
import { CoverageBanner, IconTile, Slider } from '@/components/molecules'
import type { PolicyPlanId, Policy } from '@/shared/types/global.types'
import { formatDate } from '@/shared/helpers/format-date'

type CoverageItem = {
  Icon: ComponentType<{ className?: string }>
  labelKey: string
}

const ACCIDENT_AND_ILLNESS_ITEMS: CoverageItem[] = [
  { Icon: CoverageDiagnosticsIcon, labelKey: 'diagnostics' },
  { Icon: CoverageProceduresIcon, labelKey: 'procedures' },
  { Icon: CoverageInjuriesIcon, labelKey: 'injuries' },
  { Icon: CoverageSkinConditionsIcon, labelKey: 'skinConditions' },
  { Icon: CoverageCancerTreatmentIcon, labelKey: 'cancerTreatment' },
  { Icon: CoverageXRaysIcon, labelKey: 'xRays' },
  { Icon: CoverageBloodDisordersIcon, labelKey: 'bloodDisorders' },
  { Icon: CoverageEndOfLifeCareIcon, labelKey: 'endOfLifeCare' },
]

const WELLNESS_ITEMS: CoverageItem[] = [
  { Icon: CoverageVaccinesIcon, labelKey: 'vaccines3' },
  { Icon: CoverageDeWormingIcon, labelKey: 'deworming' },
  { Icon: CoverageFecalTestIcon, labelKey: 'fecalTest' },
  { Icon: CoverageWellnessExamIcon, labelKey: 'routineWellnessExam' },
  {
    Icon: CoverageDentalCleaningIcon,
    labelKey: 'routineDentalCleaning',
  },
  { Icon: CoverageHeartwormTestIcon, labelKey: 'heartwormTest' },
]

const EARLY_YEARS_ITEMS: CoverageItem[] = [
  { Icon: CoverageVaccinesIcon, labelKey: 'vaccines6' },
  { Icon: CoverageDeWormingIcon, labelKey: 'deworming' },
  { Icon: CoverageHeartwormTestIcon, labelKey: 'heartwormTest' },
  { Icon: CoverageFecalTestIcon, labelKey: 'fecalTest' },
  { Icon: CoverageSpayNeuterIcon, labelKey: 'spayNeuter' },
  { Icon: CoverageMicrochippingIcon, labelKey: 'microchipping' },
  { Icon: CoverageWellnessExamIcon, labelKey: 'routineWellnessExam' },
  { Icon: CoverageDentalCleaningIcon, labelKey: 'routineDentalCleaning' },
]

const PLAN_COVERAGE_ITEMS: Record<PolicyPlanId, CoverageItem[]> = {
  'essential-care-2': ACCIDENT_AND_ILLNESS_ITEMS,
  'essential-1': ACCIDENT_AND_ILLNESS_ITEMS,
  'premier-care-plus-2': WELLNESS_ITEMS,
  'essential-plus-1': WELLNESS_ITEMS,
  'early-years-care-plus-2': EARLY_YEARS_ITEMS,
}

const DEFAULT_COVERAGE_ITEMS = ACCIDENT_AND_ILLNESS_ITEMS

export function PetStepThreePage({
  onBack,
  onContinue,
  policy,
}: {
  onBack: () => void
  onContinue: () => void
  policy?: Policy
}) {
  const { t } = useTranslation()

  const formattedStartDate = formatDate(policy?.startDate)

  const planName = t(
    `onboarding.pet.step3.planNames.${policy?.planId ?? 'default'}`,
    { defaultValue: t('onboarding.pet.step3.planNames.default') },
  )

  const coverageItems =
    (policy?.planId && PLAN_COVERAGE_ITEMS[policy.planId]) ||
    DEFAULT_COVERAGE_ITEMS

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto pb-4 md:flex md:flex-row md:justify-between md:pb-0">
        <div className="flex flex-col justify-between gap-6 md:max-w-98 md:pt-6">
          <div className="flex flex-col gap-4 md:max-w-90 md:gap-6">
            <H3>{t('onboarding.pet.step3.title')}</H3>
            <Text className="text-neutral-600">
              {t('onboarding.pet.step3.body')}
            </Text>
            <CoverageBanner
              planName={planName}
              policyId={
                policy?.policyExternalId ? `#${policy.policyExternalId}` : ''
              }
              startDate={formattedStartDate}
            />
          </div>
          <div className="hidden gap-3 md:mb-2.5 md:flex">
            <Button
              className="h-12"
              variant="secondary"
              onClick={onBack}
              data-testid="step-three-back"
            >
              {t('onboarding.pet.back')}
            </Button>
            <Button
              className="h-12"
              onClick={onContinue}
              data-testid="step-three-continue"
            >
              {t('onboarding.pet.continue')}
            </Button>
          </div>
        </div>

        <div className="my-auto flex w-full items-center justify-center pt-6 md:w-[45%] md:pt-0">
          <Slider
            items={coverageItems}
            renderItem={({ Icon, labelKey }) => {
              const label = t(`onboarding.pet.step3.coverageItems.${labelKey}`)
              return (
                <IconTile
                  icon={<Icon className="mt-0.5 size-15 shrink-0" />}
                  label={label}
                  iconBgColor="bg-white"
                />
              )
            }}
            itemsPerSlideDesktop={9}
            itemsPerSlideMobile={9}
          />
        </div>
      </div>

      <div className="flex w-full gap-3 pt-4 md:hidden">
        <Button
          className="h-12 w-full"
          variant="secondary"
          onClick={onBack}
          data-testid="step-three-back"
        >
          {t('onboarding.pet.back')}
        </Button>
        <Button
          className="h-12 w-full"
          onClick={onContinue}
          data-testid="step-three-continue"
        >
          {t('onboarding.pet.continue')}
        </Button>
      </div>
    </div>
  )
}
